import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";
import PageTitle from "../components/PageTitle";

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Stack>
  );
}

function P({ children }: { children: ReactNode }) {
  const theme = useTheme();
  return (
    <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
      {children}
    </Typography>
  );
}

function Bullets({ items }: { items: ReactNode[] }) {
  const theme = useTheme();
  return (
    <Box component="ul" sx={{ pl: 3, m: 0 }}>
      {items.map((item, i) => (
        <Typography
          key={i}
          component="li"
          variant="body1"
          sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );
}

export default function Terms() {
  const contactEmail = "jbreckman@prettychitty.com";

  return (
    <Box>
      <PageTitle
        title="Terms and Conditions"
        subTitle="Effective Date: April 28, 2026"
      />

      <Box sx={{ maxWidth: 760 }}>
        <Stack spacing={4}>
          <P>
            These Terms and Conditions ("Terms") govern your use of the websites
            and services operated by Pretty Chitty, LLC ("Pretty Chitty," "we,"
            "us," or "our"), including the Pretty Chitty platform and the Really
            Chitty platform (collectively, the "Service"). By accessing or using
            the Service, you agree to be bound by these Terms. If you do not
            agree, do not use the Service.
          </P>

          <Section title="1. Eligibility">
            <P>
              You must be at least 13 years old to use the Service. By using it,
              you represent that you meet this requirement and have the legal
              capacity to agree to these Terms.
            </P>
          </Section>

          <Section title="2. Accounts">
            <P>
              The Service uses Discord for authentication. You are responsible
              for maintaining the security of your Discord account and for all
              activity that occurs under your account on the Service. You agree
              to comply with Discord's terms of service.
            </P>
          </Section>

          <Section title="3. The Service">
            <Bullets
              items={[
                <>
                  <strong>Pretty Chitty</strong> offers curated board games that
                  run on our servers.
                </>,
                <>
                  <strong>Really Chitty</strong> is an open platform where
                  community members publish their own games. Games on Really
                  Chitty are created and hosted by third parties and run in your
                  browser. We do not review, approve, or vouch for community-
                  published games. <strong>Use of Really Chitty is at your own
                  risk.</strong> Buggy or malicious game code may crash your
                  browser tab, consume system resources, or behave unexpectedly.
                  Only play games made by people you trust.
                </>,
              ]}
            />
          </Section>

          <Section title="4. Pretty Chitty License (Non-Commercial Use)">
            <P>
              Pretty Chitty is provided <strong>free for personal,
              non-commercial use only.</strong> You may not use Pretty Chitty,
              the Pretty Chitty engine, or any games hosted on Pretty Chitty for
              commercial purposes, except as expressly permitted by us in
              writing. A limited exception applies to using the engine to promote
              the sale of physical board games, as described on our Develop page.
            </P>
            <P>
              If you want to use Pretty Chitty commercially, contact us at{" "}
              {contactEmail} for a separate license.
            </P>
          </Section>

          <Section title="5. User-Generated Content (Really Chitty)">
            <P>
              If you publish a game or other content on Really Chitty (your
              "Content"):
            </P>
            <Bullets
              items={[
                <>
                  <strong>You retain ownership</strong> of the rights you have in
                  your Content.
                </>,
                <>
                  <strong>You grant us a license.</strong> You grant Pretty
                  Chitty, LLC a non-exclusive, worldwide, royalty-free,
                  sublicensable license to host, store, reproduce, display,
                  distribute, transmit, cache, list, link to, and promote your
                  Content in connection with operating, providing, and marketing
                  the Service. This license continues for as long as your Content
                  is published on the Service and a reasonable period thereafter
                  to operate backups and respond to legal requests.
                </>,
                <>
                  <strong>You are responsible for your Content.</strong> You
                  represent that you have all necessary rights in your Content
                  and that your Content does not infringe or violate the rights
                  of any third party or any law.
                </>,
                <>
                  <strong>We may remove anything.</strong> We may remove,
                  restrict, or refuse to display any Content for any reason, at
                  our sole discretion, including for violations of these Terms or
                  applicable law.
                </>,
              ]}
            />
          </Section>

          <Section title="6. Ownership of Games">
            <P>
              Each game made available through the Service is the property of
              its respective publisher, designer, or creator. All trademarks,
              names, logos, rules, artwork, and other materials associated with
              a game are owned by their respective rights-holders.
            </P>
            <Bullets
              items={[
                <>
                  <strong>Pretty Chitty curated games</strong> are made available
                  under licenses or permissions granted by their publishers or
                  designers. Nothing in these Terms or your use of the Service
                  transfers any ownership or rights in those games to you, and
                  you may not copy, redistribute, or create derivative works from
                  them except as permitted by the relevant rights-holder or by
                  law.
                </>,
                <>
                  <strong>Really Chitty community games</strong> are owned by
                  their creators, who grant Pretty Chitty the license described
                  in Section 5. The right to play a Really Chitty game is granted
                  by its creator, not by Pretty Chitty.
                </>,
              ]}
            />
            <P>
              The Service itself — including the website code, branding, and the
              Pretty Chitty engine (subject to Section 4 and the engine's own
              license terms) — is owned by or licensed to Pretty Chitty, LLC.
            </P>
          </Section>

          <Section title="7. Acceptable Use">
            <P>You agree not to:</P>
            <Bullets
              items={[
                "Use the Service for any unlawful purpose or in violation of any applicable law or regulation;",
                "Submit, publish, or distribute content that is illegal, infringing, defamatory, harassing, hateful, threatening, sexually exploitative of minors, or that depicts or promotes violence against real people;",
                "Upload or distribute malware, viruses, or game code intended to harm, surveil, or extract data from other users without their informed consent;",
                "Attempt to gain unauthorized access to the Service, other users' accounts, or any underlying systems;",
                "Interfere with, disrupt, overload, or attack the Service or its infrastructure;",
                "Scrape, crawl, or systematically extract data from the Service without our prior written permission;",
                "Use the Service to harass, abuse, or harm other users;",
                "Misrepresent yourself or impersonate any person or entity;",
                "Circumvent any access restrictions, rate limits, or technical measures we use.",
              ]}
            />
          </Section>

          <Section title="8. Copyright and DMCA">
            <P>
              We respect intellectual property rights and respond to clear
              notices of alleged copyright infringement under the U.S. Digital
              Millennium Copyright Act ("DMCA"). To submit a notice, send the
              following to {contactEmail}:
            </P>
            <Bullets
              items={[
                "A physical or electronic signature of the copyright owner or authorized agent;",
                "Identification of the copyrighted work claimed to be infringed;",
                "Identification of the material claimed to be infringing and its location on the Service (URL or sufficient detail);",
                "Your contact information (address, telephone number, email);",
                "A statement that you have a good-faith belief that the use is not authorized by the copyright owner, its agent, or the law;",
                "A statement, under penalty of perjury, that the information in the notice is accurate and that you are authorized to act on the copyright owner's behalf.",
              ]}
            />
            <P>
              We may remove allegedly infringing material and terminate accounts
              of repeat infringers.
            </P>
          </Section>

          <Section title="9. Termination">
            <P>
              We may suspend or terminate your access to the Service at any time,
              with or without notice, for any reason, including violation of
              these Terms. You may stop using the Service at any time. Provisions
              that by their nature should survive termination will survive,
              including ownership, disclaimers, limitations of liability,
              indemnification, and dispute resolution.
            </P>
          </Section>

          <Section title="10. Disclaimers">
            <P>
              The Service is provided <strong>"as is" and "as available"</strong>{" "}
              without warranties of any kind, either express or implied,
              including without limitation implied warranties of merchantability,
              fitness for a particular purpose, non-infringement, and any
              warranties arising from course of dealing or usage of trade. We do
              not warrant that the Service will be uninterrupted, error-free,
              secure, or free of harmful components.
            </P>
            <P>
              <strong>Really Chitty specifically:</strong> community games run
              untrusted code in your browser. We make no representations or
              warranties about community-published games, their content,
              behavior, or safety. You assume all risk of using Really Chitty and
              any games published on it.
            </P>
          </Section>

          <Section title="11. Limitation of Liability">
            <P>
              To the maximum extent permitted by law, Pretty Chitty, LLC and its
              officers, directors, employees, and agents will not be liable for
              any indirect, incidental, special, consequential, exemplary, or
              punitive damages, or any loss of profits, revenue, data, or
              goodwill, arising out of or related to your use of the Service. Our
              total liability for any claims arising out of or related to these
              Terms or the Service will not exceed one hundred U.S. dollars
              ($100).
            </P>
          </Section>

          <Section title="12. Indemnification">
            <P>
              You agree to indemnify and hold harmless Pretty Chitty, LLC and its
              affiliates, officers, directors, employees, and agents from and
              against any claims, liabilities, damages, losses, and expenses
              (including reasonable attorneys' fees) arising out of or relating
              to: (a) your Content; (b) your use of the Service; (c) your
              violation of these Terms; or (d) your violation of any rights of
              another.
            </P>
          </Section>

          <Section title="13. Governing Law and Disputes">
            <P>
              These Terms are governed by the laws of the State of New Jersey,
              USA, without regard to its conflict-of-laws principles. You agree
              that any dispute arising out of or relating to these Terms or the
              Service will be brought exclusively in the state or federal courts
              located in New Jersey, and you consent to the personal jurisdiction
              of those courts.
            </P>
          </Section>

          <Section title="14. Changes to These Terms">
            <P>
              We may update these Terms from time to time. If we make material
              changes, we will update the effective date above and, where
              appropriate, provide additional notice. Your continued use of the
              Service after changes become effective constitutes your acceptance
              of the updated Terms.
            </P>
          </Section>

          <Section title="15. Miscellaneous">
            <P>
              These Terms, together with the Privacy Policy, are the entire
              agreement between you and Pretty Chitty regarding the Service. If
              any provision is held unenforceable, the remaining provisions will
              remain in full force and effect. Our failure to enforce any
              provision is not a waiver of our right to do so later. You may not
              assign these Terms without our prior written consent; we may assign
              them freely.
            </P>
          </Section>

          <Section title="16. Contact">
            <P>
              Pretty Chitty, LLC
              <br />
              New Jersey, USA
              <br />
              {contactEmail}
            </P>
          </Section>
        </Stack>
      </Box>
    </Box>
  );
}
